import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { BankAPI } from '../../../../../../../api/bancos.api'
import ModalCreateCreditCard from './ModalCreateCreditCard'
import { maskCardLabel } from './creditCard.utils'

const bankAPI = new BankAPI()

const CreditCardSelect = ({ businessId, value, onChange, disabled }) => {
  const queryClient = useQueryClient()
  const containerRef = useRef(null)

  const [searchText, setSearchText] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const creditCardsQuery = useQuery({
    queryKey: ['credit-cards', businessId],
    queryFn: async () => {
      const response = await bankAPI.getCreditCards(businessId)
      return response.data
    },
    enabled: Boolean(businessId)
  })

  useEffect(() => {
    if (value) {
      setSearchText(maskCardLabel(value))
    }
  }, [value])

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false)
        if (value) {
          setSearchText(maskCardLabel(value))
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value])

  const creditCards = creditCardsQuery.data || []
  const filteredCreditCards = creditCards.filter(card => {
    const label = maskCardLabel(card).toLowerCase()
    return label.includes(searchText.toLowerCase().trim())
  })

  const handleSelectCard = card => {
    onChange(card)
    setSearchText(maskCardLabel(card))
    setShowDropdown(false)
  }

  const handleOpenCreateModal = () => {
    setShowDropdown(false)
    setShowCreateModal(true)
  }

  const handleCardCreated = created => {
    queryClient.invalidateQueries({ queryKey: ['credit-cards', businessId] })
    onChange(created)
    setSearchText(maskCardLabel(created))
    setShowCreateModal(false)
  }

  return (
    <div ref={containerRef} className='relative'>
      <input
        type='text'
        value={searchText}
        onChange={event => {
          setSearchText(event.target.value)
          setShowDropdown(true)
        }}
        onFocus={() => {
          setShowDropdown(true)
          if (value) {
            setSearchText('')
          }
        }}
        disabled={disabled}
        placeholder='Busca una tarjeta guardada...'
        className='w-full h-10 border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 rounded-full shadow-sm leading-tight text-sm font-sans bg-white disabled:opacity-60 focus:outline-none focus:ring-0'
      />

      {showDropdown && !disabled && (
        <div className='absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-md z-50 overflow-hidden'>
          <ul className='max-h-48 overflow-y-auto'>
            {filteredCreditCards.length === 0 && (
              <li className='px-3 py-2 text-sm text-slate-400'>
                {creditCardsQuery.isLoading ? 'Cargando tarjetas...' : 'No tienes tarjetas guardadas'}
              </li>
            )}
            {filteredCreditCards.map(card => (
              <li
                key={card.id}
                onClick={() => handleSelectCard(card)}
                className='px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-sans'
              >
                {maskCardLabel(card)}
              </li>
            ))}
          </ul>

          <button
            type='button'
            onClick={handleOpenCreateModal}
            className='w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-accent2 border-t border-gray-200 hover:bg-gray-50'
          >
            <Plus size={14} />
            Agregar tarjeta nueva
          </button>
        </div>
      )}

      <ModalCreateCreditCard
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        businessId={businessId}
        onCreated={handleCardCreated}
      />
    </div>
  )
}

export default CreditCardSelect
